/**
 * CommunityScreen.jsx
 * Leaderboard, challenges, and social features with REAL DATA from backend
 */
import React, { useState, useEffect } from 'react';
import { 
  Trophy, Target, Users, RefreshCw, ChevronRight, Flame, 
  Droplets, Dumbbell, Apple, Crown, Medal, Award, Check, X, Plus
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CommunityScreen = ({ portalToken, customerId }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userPosition, setUserPosition] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [challenges, setChallenges] = useState([]);
  const [period, setPeriod] = useState('weekly');
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [joiningChallenge, setJoiningChallenge] = useState(false);

  useEffect(() => {
    if (portalToken) {
      loadData();
    }
  }, [portalToken, period]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load leaderboard
      const leaderboardRes = await axios.get(
        `${API}/portal/community/leaderboard?period=${period}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setUserPosition(leaderboardRes.data.user_position);
      setTotalParticipants(leaderboardRes.data.total_participants || 0);

      // Load challenges
      const challengesRes = await axios.get(
        `${API}/portal/community/challenges`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      // API returns array directly or { challenges: [...] }
      const challengeData = Array.isArray(challengesRes.data) 
        ? challengesRes.data 
        : challengesRes.data.challenges || [];
      setChallenges(challengeData);
    } catch (error) {
      console.error('Error loading community data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    setJoiningChallenge(true);
    try {
      await axios.post(
        `${API}/portal/community/challenges/${challengeId}/join`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      toast.success('Challenge beigetreten!');
      loadData();
      setSelectedChallenge(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Beitreten');
    } finally {
      setJoiningChallenge(false);
    }
  };

  const getChallengeIcon = (type) => {
    switch (type) {
      case 'nutrition': return <Apple className="w-5 h-5 text-green-400" />;
      case 'water': return <Droplets className="w-5 h-5 text-blue-400" />;
      case 'workout': return <Dumbbell className="w-5 h-5 text-red-400" />;
      default: return <Target className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-zinc-400" />;
      case 3: return <Award className="w-5 h-5 text-amber-600" />;
      default: return null;
    }
  };

  const formatDaysLeft = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? `${days} Tage übrig` : 'Endet heute';
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('community.title')}</h1>
            <p className="text-zinc-400 text-xs">{totalParticipants} aktive Mitglieder</p>
          </div>
        </div>
        <button 
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
    
      {/* Tabs */}
      <div className="flex gap-2 bg-zinc-800/50 p-1 rounded-xl">
        <button 
          onClick={() => setActiveTab('leaderboard')}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
            activeTab === 'leaderboard' 
              ? 'bg-yellow-500 text-black' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <Trophy className="w-3.5 h-3.5" />
          Rangliste
        </button>
        <button 
          onClick={() => setActiveTab('challenges')}
          className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
            activeTab === 'challenges' 
              ? 'bg-yellow-500 text-black' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <Target className="w-3.5 h-3.5" />
          Challenges
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">{t('community.title')}...</p>
        </div>
      ) : activeTab === 'leaderboard' ? (
        <>
          {/* Period Selector */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              { id: 'weekly', label: 'Woche' },
              { id: 'monthly', label: 'Monat' },
              { id: 'all-time', label: 'Gesamt' }
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap ${
                  period === p.id 
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                    : 'bg-zinc-800 text-zinc-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* User Position */}
          {userPosition && (
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/10 rounded-xl p-4 border border-yellow-500/30">
              <p className="text-zinc-400 text-xs mb-2">{t('community.your_rank')}</p>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-yellow-400">#{userPosition.rank}</span>
                <div className="flex-1">
                  <p className="text-white font-medium">{userPosition.points} Punkte</p>
                  <div className="flex items-center gap-1 text-zinc-400 text-xs">
                    <Flame className="w-3 h-3 text-orange-400" />
                    <span>{userPosition.streak} Tage Streak</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard List */}
          <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
            <div className="p-3 border-b border-zinc-700/50">
              <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Top 10</p>
            </div>
            {leaderboard.slice(0, 10).map((user, i) => (
              <div 
                key={user.id || i} 
                className={`flex items-center gap-3 p-3 border-b border-zinc-700/30 last:border-0 ${
                  user.id === userPosition?.id ? 'bg-yellow-500/10' : ''
                }`}
              >
                <div className="w-8 text-center">
                  {getRankIcon(user.rank || i + 1) || (
                    <span className="text-zinc-500 font-medium">{user.rank || i + 1}</span>
                  )}
                </div>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: user.avatar_color || '#3B82F6' }}
                >
                  {user.name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{user.name}</p>
                  <div className="flex items-center gap-1 text-zinc-500 text-xs">
                    <Flame className="w-3 h-3 text-orange-400" />
                    <span>{user.streak} Tage</span>
                  </div>
                </div>
                <span className="text-yellow-400 font-bold">{user.points}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* Challenges List */}
          <div className="space-y-3">
            {challenges.length === 0 ? (
              <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
                <Target className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-white font-medium">{t('community.no_challenges')}</p>
                <p className="text-zinc-400 text-sm mt-1">{t('community.challenges')}...</p>
              </div>
            ) : (
              challenges.map(challenge => (
                <button
                  key={challenge.id}
                  onClick={() => setSelectedChallenge(challenge)}
                  className="w-full bg-zinc-800/50 rounded-xl p-4 text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-zinc-700/50 flex items-center justify-center">
                      {getChallengeIcon(challenge.challenge_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium text-sm truncate">{challenge.name}</h3>
                        {challenge.is_joined && (
                          <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded">
                            Dabei
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-400 text-xs line-clamp-1">{challenge.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-zinc-500 text-xs flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {challenge.participants_count}
                        </span>
                        <span className="text-zinc-500 text-xs">
                          {formatDaysLeft(challenge.end_date)}
                        </span>
                        <span className="text-yellow-400 text-xs font-medium">
                          +{challenge.reward_points} Punkte
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-500" />
                  </div>
                  
                  {challenge.is_joined && challenge.progress !== undefined && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-400">Fortschritt</span>
                        <span className="text-yellow-400">{Math.round((challenge.progress / challenge.target_value) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-500 rounded-full transition-all"
                          style={{ width: `${Math.min((challenge.progress / challenge.target_value) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Challenge Detail Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
          <div className="w-full bg-zinc-900 rounded-t-3xl p-5 space-y-4 animate-slide-up max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                  {getChallengeIcon(selectedChallenge.challenge_type)}
                </div>
                <div>
                  <h3 className="text-white font-semibold">{selectedChallenge.name}</h3>
                  <p className="text-zinc-400 text-xs">{selectedChallenge.participants_count} Teilnehmer</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedChallenge(null)}
                className="p-1 text-zinc-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-zinc-300 text-sm">{selectedChallenge.description}</p>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] mb-1">Ziel</p>
                <p className="text-white font-bold">{selectedChallenge.target_value}</p>
                <p className="text-zinc-500 text-xs">{selectedChallenge.target_unit}</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] mb-1">Dauer</p>
                <p className="text-white font-bold">{selectedChallenge.duration_days}</p>
                <p className="text-zinc-500 text-xs">Tage</p>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 text-center">
                <p className="text-zinc-500 text-[10px] mb-1">Belohnung</p>
                <p className="text-yellow-400 font-bold">+{selectedChallenge.reward_points}</p>
                <p className="text-zinc-500 text-xs">Punkte</p>
              </div>
            </div>
            
            {selectedChallenge.is_joined ? (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Du nimmst teil!</span>
                </div>
                <p className="text-zinc-400 text-sm">
                  Dein Fortschritt wird automatisch getrackt. Bleib dran!
                </p>
              </div>
            ) : (
              <button
                onClick={() => handleJoinChallenge(selectedChallenge.id)}
                disabled={joiningChallenge}
                className="w-full py-3 bg-yellow-500 text-black rounded-xl font-medium flex items-center justify-center gap-2"
              >
                {joiningChallenge ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Challenge beitreten
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityScreen;
